import type { Order } from "../../api/types";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { canRoute, kindOf, paymentOf } from "./order-view";

interface Props {
  orders: Order[];
  /** A tap on one order in the list: only that one stays chosen, with its receipt. */
  onOnly: (order: Order) => void;
  /**
   * The total and the total per payment: only in Beheer. At the till anyone can tick everything, and the
   * day's takings are not for everyone to see (Furkan, 09/10/2026).
   */
  totals: boolean;
}

/**
 * The right side with several orders chosen (prototype ordPreviewHtml): the total, the total per
 * payment, the deliveries for a route and a short list; a tap on one shows only that receipt.
 */
export function OrdersOverview({ orders, onOnly, totals }: Props) {
  const text = t.orders.selection;
  const counted = orders.filter((order) => order.status !== "cancelled");
  const byPayment = new Map<string, number>();
  for (const order of counted) {
    const label = order.payments.length === 0 ? t.orders.unpaid : paymentOf(order);
    byPayment.set(label, (byPayment.get(label) ?? 0) + order.totalCents);
  }
  return (
    <aside className="bonpanel" aria-label={text.chosen(orders.length)}>
      <div className="ordph">
        <b>{text.chosen(orders.length)}</b>
      </div>
      <div className="ordsum">
        {totals && (
          <>
            <div className="kv">
              <span>{text.total}</span>
              <b className="num">{formatEuro(counted.reduce((total, order) => total + order.totalCents, 0))}</b>
            </div>
            {[...byPayment].map(([label, cents]) => (
              <div className="kv" key={label}>
                <span>{label}</span>
                <span className="num">{formatEuro(cents)}</span>
              </div>
            ))}
          </>
        )}
        <div className="kv">
          <span>{text.forRoute}</span>
          <span>{orders.filter(canRoute).length}</span>
        </div>
      </div>
      <div className="ordmini">
        {orders.map((order) => (
          <button
            key={order.id}
            type="button"
            className="ordmi"
            onClick={() => {
              onOnly(order);
            }}
          >
            <span className="code">{order.code}</span>
            <span>{kindOf(order)}</span>
            <span className="num">{formatEuro(order.totalCents)}</span>
          </button>
        ))}
      </div>
      <p className="proto">{text.pickOne}</p>
    </aside>
  );
}
